import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  Group, Radio, Rating, Stack, Text,
} from '@mantine/core';
import { CustomReviewFields } from '../../../models/product.types';

interface ReviewCustomFields {
  taste: number;
  convenience: number;
  health: number;
  portions: number;
  orderAgain: string;
}

export interface ReviewCustomFieldsFormHandle {
  getPayload: () => CustomReviewFields;
}

const orderAgainMapping: Record<string, number> = {
  definitely: 5,
  probably: 4,
  not_sure: 3,
  unlikely: 2,
  no: 1,
};

export const ReviewCustomFieldsForm = forwardRef<ReviewCustomFieldsFormHandle>((_, ref) => {
  const [fields, setFields] = useState<ReviewCustomFields>({
    taste: 0,
    convenience: 0,
    health: 0,
    portions: 0,
    orderAgain: '',
  });

  const handleRatingChange = (key: keyof ReviewCustomFields, value: number | string) => {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useImperativeHandle(ref, () => ({
    getPayload: () => ({
      '--125127': fields.taste > 0 ? String(fields.taste) : undefined,
      '--125128': fields.convenience > 0 ? String(fields.convenience) : undefined,
      '--125129': fields.health > 0 ? String(fields.health) : undefined,
      '--125130': fields.portions > 0 ? String(fields.portions) : undefined,
      '--125115': fields.orderAgain ? String(orderAgainMapping[fields.orderAgain]) : undefined,
    }),
  }));

  return (
    <Stack
      spacing="md"
      style={{
        padding: '20px 20px 20px 0 ',
      }}
    >
      <Group position="apart" spacing={5} style={{ alignItems: 'center' }}>
        <Text size="sm">On a scale of 1-5, how tasty/flavorful was this item?</Text>
        <Rating value={fields.taste} onChange={(val) => handleRatingChange('taste', val)} sx={{ alignSelf: 'center', '& label': { marginBottom: 0 } }} />
      </Group>

      <Group position="apart" spacing={5} style={{ alignItems: 'center' }}>
        <Text size="sm">On a scale of 1-5, how convenient was this item to prepare?</Text>
        <Rating
          value={fields.convenience}
          onChange={(val) => handleRatingChange('convenience', val)}
          sx={{ alignSelf: 'center', '& label': { marginBottom: 0 } }}
        />
      </Group>

      <Group position="apart" spacing={5} style={{ alignItems: 'center' }}>
        <Text size="sm">On a scale of 1-5, how healthy/nutritious was this item?</Text>
        <Rating value={fields.health} onChange={(val) => handleRatingChange('health', val)} sx={{ alignSelf: 'center', '& label': { marginBottom: 0 } }} />
      </Group>

      <Group position="apart" spacing={5} style={{ alignItems: 'center' }}>
        <Text size="sm">On a scale of 1-5, how was the portion size for this item?</Text>
        <Rating value={fields.portions} onChange={(val) => handleRatingChange('portions', val)} sx={{ alignSelf: 'center', '& label': { marginBottom: 0 } }} />
      </Group>

      <Stack spacing={4}>
        <Text size="sm">Would you order this item again if it appeared on the menu?</Text>
        <Radio.Group
          value={fields.orderAgain}
          onChange={(val) => handleRatingChange('orderAgain', val)}
          name="orderAgain"
        >
          <Group mt={0} spacing={12}>
            <Radio value="definitely" label="Definitely" size="xs" styles={{ label: { paddingLeft: 6, fontSize: 14, marginBottom: 0 }, radio: { cursor: 'pointer' }, inner: { alignSelf: 'center', marginTop: 0 }, body: { alignItems: 'center' } }} />
            <Radio value="probably" label="Probably" size="xs" styles={{ label: { paddingLeft: 6, fontSize: 14, marginBottom: 0 }, radio: { cursor: 'pointer' }, inner: { alignSelf: 'center', marginTop: 0 }, body: { alignItems: 'center' } }} />
            <Radio value="not_sure" label="Not sure" size="xs" styles={{ label: { paddingLeft: 6, fontSize: 14, marginBottom: 0 }, radio: { cursor: 'pointer' }, inner: { alignSelf: 'center', marginTop: 0 }, body: { alignItems: 'center' } }} />
            <Radio value="unlikely" label="Unlikely" size="xs" styles={{ label: { paddingLeft: 6, fontSize: 14, marginBottom: 0 }, radio: { cursor: 'pointer' }, inner: { alignSelf: 'center', marginTop: 0 }, body: { alignItems: 'center' } }} />
            <Radio value="no" label="No" size="xs" styles={{ label: { paddingLeft: 6, fontSize: 14, marginBottom: 0 }, radio: { cursor: 'pointer' }, inner: { alignSelf: 'center', marginTop: 0 }, body: { alignItems: 'center' } }} />
          </Group>
        </Radio.Group>
      </Stack>
    </Stack>
  );
});
